/**
 * Selection Helper Utilities
 *
 * Provides utility functions for normalizing and handling time selections
 * in both single-mode and multi-mode scenarios.
 * Works with SelectionTimeRange type: {start: Date, end: Date, allday: boolean}
 */

/**
 * Represents a time range selection
 */
export interface SelectionTimeRange {
  start: Date
  end: Date
  allday: boolean
}

/**
 * Normalize selection prop to consistent array format
 *
 * @param selectionProp - Selection from props (single object or array)
 * @param multiMode - Whether in multi-selection mode
 * @returns Normalized array of selections
 */
export const normalizeSelectionProp = (
  selectionProp: SelectionTimeRange | SelectionTimeRange[] | null,
  multiMode: boolean,
): SelectionTimeRange[] => {
  if (!selectionProp) return []

  // Multi-mode: expect array
  if (multiMode) {
    return Array.isArray(selectionProp) ? selectionProp : []
  }

  // Single-mode: convert object to single-item array
  return Array.isArray(selectionProp) ? selectionProp : [selectionProp]
}

/**
 * Get active selection based on mode and interaction state
 *
 * During drag/resize operations, returns the temporary dragging state.
 * Otherwise returns the prop value.
 *
 * @param selectionProp - Selection from props
 * @param tempDragState - Temporary state during drag/resize
 * @param isDragging - Whether currently dragging or resizing
 * @param multiMode - Whether in multi-selection mode
 * @returns Active selections array
 */
export const getActiveSelection = (
  selectionProp: SelectionTimeRange | SelectionTimeRange[] | null,
  tempDragState: SelectionTimeRange | null,
  isDragging: boolean,
  multiMode: boolean,
): SelectionTimeRange[] => {
  // During drag/resize in single-mode, use temp state for real-time feedback
  if (isDragging && !multiMode && tempDragState) {
    return [tempDragState]
  }

  // Otherwise use prop value
  return normalizeSelectionProp(selectionProp, multiMode)
}

/**
 * Check if selection should be shown for current date
 *
 * @param selectionStart - Selection start time
 * @param selectionEnd - Selection end time
 * @param currentDate - Current date being displayed
 * @returns Whether selection overlaps with current date
 */
export const shouldShowSelection = (
  selectionStart: Date,
  selectionEnd: Date,
  currentDate: Date,
): boolean => {
  const dayStart = new Date(currentDate)
  dayStart.setHours(0, 0, 0, 0)

  const dayEnd = new Date(currentDate)
  dayEnd.setHours(23, 59, 59, 999)

  return selectionStart < dayEnd && selectionEnd > dayStart
}

/**
 * Get selection portion for specific date
 *
 * When selection spans multiple days, returns the portion that overlaps with target date
 *
 * @param selectionStart - Selection start time
 * @param selectionEnd - Selection end time
 * @param targetDate - Target date to check
 * @returns {start, end} for the date portion, or null if no overlap
 */
export const getSelectionForDate = (
  selectionStart: Date,
  selectionEnd: Date,
  targetDate: Date,
): { start: Date; end: Date } | null => {
  const dayStart = new Date(targetDate)
  dayStart.setHours(0, 0, 0, 0)

  const dayEnd = new Date(targetDate)
  dayEnd.setHours(23, 59, 59, 999)

  // Check if selection overlaps with this date
  if (selectionStart >= dayEnd || selectionEnd <= dayStart) {
    return null
  }

  // Calculate intersection
  const displayStart = selectionStart > dayStart ? selectionStart : dayStart
  const displayEnd = selectionEnd < dayEnd ? selectionEnd : dayEnd

  return {
    start: displayStart,
    end: displayEnd,
  }
}

/**
 * Format selections array for callback
 *
 * Ensures consistent format when calling parent callbacks
 *
 * @param selections - Current selections array
 * @param multiMode - Whether in multi-mode
 * @returns Single object for single-mode, array for multi-mode
 */
export const formatSelectionsForCallback = (
  selections: SelectionTimeRange[],
  multiMode: boolean,
): SelectionTimeRange | SelectionTimeRange[] | null => {
  if (multiMode) {
    return selections
  }

  // Single-mode: return single object or null
  return selections.length > 0 ? selections[0] : null
}

/**
 * Add new selection to array
 *
 * @param currentSelections - Current selections
 * @param newSelection - New selection {start: Date, end: Date, allday: boolean}
 * @param multiMode - Whether in multi-mode
 * @returns Updated selections array
 */
export const addSelection = (
  currentSelections: SelectionTimeRange[],
  newSelection: SelectionTimeRange,
  multiMode: boolean,
): SelectionTimeRange[] => {
  if (multiMode) {
    return [...currentSelections, newSelection]
  }

  // Single-mode: replace existing selection
  return [newSelection]
}

/**
 * Remove selection from array by index
 *
 * @param currentSelections - Current selections
 * @param index - Index to remove
 * @returns Updated selections array
 */
export const removeSelection = (
  currentSelections: SelectionTimeRange[],
  index: number,
): SelectionTimeRange[] => {
  return currentSelections.filter((_, i) => i !== index)
}

/**
 * Clear all selections
 *
 * @returns Empty array
 */
export const clearAllSelections = (): SelectionTimeRange[] => {
  return []
}
